import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import {
  DownloadFile,
  EuroIcon,
  InformationIcon,
  LocationTwoIcon,
  PlusIcon,
  UploadIcon
} from '@repo/ui/icons'
import Lock from '@repo/ui/lock'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import {
  publishJourneys,
  unpublishJourneys,
  updateJourneyBasePrice
} from '../../api/products/update-journey'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../components/elements/information-tooltip'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { JourneyUpdateStatusForm } from '../../components/pages/products/journey-update-status-form'
import { JourneysFilter } from '../../components/pages/products/journeys-filter'
import { ProductDatePriceForm } from '../../components/pages/products/product-date-price-form'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { Journey, JourneyFilters } from '../../lib/types/models/product/journey'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformJourneyFiltersToUrl,
  transformUrlToJourneyFilters
} from '../../lib/utils/filters-mapping/journey'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

export const journeyInitialFilter: JourneyFilters = {
  years: [new Date().getFullYear()]
}

export function JourneyListPage() {
  const { navigate } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<Journey>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { toggleModal, modalState, closeModel, selectedData } = useModalManager<
    'filters' | 'editStatus' | 'editPrice',
    Journey[]
  >(['filters', 'editStatus', 'editPrice'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<JourneyFilters>({
      initialFilters: journeyInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformJourneyFiltersToUrl,
      transformUrlToFilter: transformUrlToJourneyFilters
    })

  const { mutateAsync: publishJourneysMutation } = useMutation({
    mutationFn: publishJourneys,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Séjours publiés avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: unpublishJourneysMutation } = useMutation({
    mutationFn: unpublishJourneys,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Séjours dépubliés avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: updateJourneyBasePriceMutation } = useMutation({
    mutationFn: updateJourneyBasePrice,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Prix de base modifié avec succès')
      tableRef.current?.refetch()
      closeModel('editPrice')
    }
  })

  const columns: Column<Journey>[] = [
    {
      accessorKey: 'published',
      header: 'Statut',
      enableSorting: true,
      cell: ({ row }) =>
        !row.original.publishedUpdatedAt ? (
          <Badge text={'Non publié'} variant={'danger'} />
        ) : Boolean(row.original.published) ? (
          <Badge
            text={`Publié - ${DateFormatter.DDMMYYYY(row.original.publishedUpdatedAt)}`}
            variant={'success'}
          />
        ) : (
          <Badge
            text={`Dépublié - ${DateFormatter.DDMMYYYY(row.original.publishedUpdatedAt)}`}
            variant={'danger'}
          />
        )
    },
    {
      accessorKey: 'reference',
      header: 'Référence séjour',
      enableSorting: true
    },

    {
      accessorKey: 'date.product.country.name',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => (
        <div className={'flex items-center gap-2 w-20'}>
          <CountryFlag code={row.original.date?.product?.country.code} />
        </div>
      )
    },
    {
      accessorKey: 'date.product.formula.title',
      header: 'Formule',
      enableSorting: true,
      cell: ({ row }) => getTranslatedName(row.original.date.product.formula)
    },
    {
      accessorKey: '',
      header: 'Prix de base',
      enableSorting: true,
      cell: ({ row }) => `${NumberFormatter.roundNumber(row.original.date.basePrice)}€`
    },
    {
      accessorKey: 'mainOption.title',
      header: 'Thème',
      enableSorting: true,
      cell: ({ row }) => getTranslatedName(row.original.mainOption)
    },
    {
      accessorKey: 'mainOptionPrice',
      header: 'Tarif thème',
      enableSorting: true,
      cell: ({ row }) => `${NumberFormatter.roundNumber(row.original.mainOptionPrice)}€`
    },
    {
      accessorKey: 'secondaryOptions',
      header: 'Option(s) facultative(s)',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2 py-2">
          {row.original.secondaryOptions.map((option) => (
            <Badge
              key={option.id}
              text={`${getTranslatedName(option.option)} - ${option.price}€`}
            />
          ))}
        </div>
      )
    },
    {
      accessorKey: '',
      header: 'Tarif options',
      enableSorting: true
    },
    {
      accessorKey: 'date.period.name',
      header: 'Période',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.date.period ? <PeriodBadge period={row.original.date.period} /> : '-'
    },
    {
      accessorKey: 'date.departureDate',
      header: 'Date de début',
      enableSorting: true,
      cell: ({ row }) => <TableDate date={row.original.date.departureDate} />
    },
    {
      accessorKey: 'date.returnDate',
      header: 'Date de fin',
      enableSorting: true,
      cell: ({ row }) => <TableDate date={row.original.date.returnDate} />
    },
    {
      accessorKey: 'date.durationInDays',
      header: 'Durée',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant={'warning'}
          text={`${row.original.date.durationInDays}jours/ ${row.original.date.durationInDays - 1}nuits`}
        />
      )
    },
    {
      accessorKey: 'totalPrice',
      header: 'Prix total',

      enableSorting: true,
      headerInfo: (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <InformationIcon width="20px" height="20px" color={tailwindColors.secondary.DEFAULT} />
          }
          size="small"
          contentClassName="p-2"
        >
          <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
            prix de base + tarif thème + options facultatives +<br />
            FA1E ou FA2E (la moins chère est appliquée si les
            <br />
            deux existent, sinon celle disponible)
          </Typography>
        </InformationToolTip>
      ),
      cell: ({ row }) => `${row.original.totalPrice} €`
    },
    {
      accessorKey: 'date.locked',
      header: 'Verrouillé',
      enableSorting: true,
      cell: ({ row }) => <Lock isLocked={Boolean(row.original.date.locked)} />
    }
  ]

  const journeyDateIds = useMemo(() => {
    if (!selectedData) return []
    const dates = selectedData.map((journey) => journey.date).flat()
    return dates.filter((date, index) => dates.indexOf(date) === index).map((date) => date.id)
  }, [selectedData])

  return (
    <div>
      <UserNavBar title={'Consulter les séjours'} icon={LocationTwoIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Journey, JourneyFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Tous les séjours'}
        deleteMessage={{ name: 'lieu de séjour', attributeName: 'reference' }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(journey) =>
          navigate(
            `${Paths.PRODUCT_UPDATE_PAGE.replace(':id', journey.date.product.id.toString())}?scroll-to=${journey.id}`,
            { state: { ref: 'journey' } }
          )
        }
        endpoint={API.products.getAllJourneys.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'multiple',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Modifier les prix de base',
            fn: (journeys) => toggleModal('editPrice', journeys)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Exporter',
            fn: (journeys) => {
              const ids = journeys.map((journey) => journey.id)
              downloadDocuments({
                endpoint: API.products.exportJourneysExcel.url,
                ids
              })
            }
          },
          {
            type: 'multiple',
            icon: DownloadFile,
            variant: 'ghost',
            label: 'Publier/Dépublier',
            fn: (journeys) => toggleModal('editStatus', journeys)
          }
        ]}
      />

      {selectedData && (
        <>
          <Modal
            isOpen={modalState.editPrice}
            title={'Modifier les prix de base'}
            icon={EuroIcon}
            hideActions
            onCancel={() => closeModel('editPrice')}
          >
            <ProductDatePriceForm
              className={MODAL_BODY_CLASSNAME}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('editPrice'),
                onConfirm: (values) =>
                  updateJourneyBasePriceMutation({ ...values, ids: journeyDateIds })
              })}
            />
          </Modal>

          <Modal
            isOpen={modalState.editStatus}
            title={'Publier ou Dépublier des Séjours'}
            icon={DownloadFile}
            hideActions
            onCancel={() => closeModel('editStatus')}
          >
            <JourneyUpdateStatusForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={{
                journeyIds: selectedData.map((journey) => journey.id)
              }}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('editStatus'),
                onConfirm: async (values) => {
                  if (values.status === 'publish') await publishJourneysMutation(values.journeyIds)
                  else await unpublishJourneysMutation(values.journeyIds)

                  closeModel('editStatus')
                }
              })}
            />
          </Modal>
        </>
      )}

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <JourneysFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
